import { Injectable } from "@angular/core";
import { Block } from "../types/block";
import { JournalItemEntry } from "../types/journal-item";
import {
  CHECKBOX,
  CONTAINER,
  FREE_TEXT,
  HTML,
  RADIO,
  SELECT,
  SLIDER,
  SMART_CHECKBOX,
  SMART_RADIO,
  TEXT,
} from "../values/blocks";

export interface SerializedJournalEntry {
  id: string;
  date: string;
  courseSession?: string;
  text: string;
}

const PER_ENTRY_CHAR_LIMIT = 1500;

@Injectable({ providedIn: "root" })
export class JournalEntrySerializerService {
  serialize(entry: JournalItemEntry): SerializedJournalEntry | null {
    const id = entry._id?.toString?.() ?? "";
    if (!id) return null;

    const lines: string[] = [];
    for (const block of entry.block_list ?? []) {
      const rendered = this._renderBlock(block);
      if (rendered) lines.push(rendered);
    }
    if (lines.length === 0) return null;

    let text = lines.join("\n");
    if (text.length > PER_ENTRY_CHAR_LIMIT) {
      text = text.slice(0, PER_ENTRY_CHAR_LIMIT - 1).trimEnd() + "…";
    }

    const courseSession = [entry.course_title, entry.course_session_title]
      .filter((p): p is string => !!p && p.trim().length > 0)
      .join(" — ");

    return {
      id,
      date: entry.completed_date ?? entry.created_date ?? "",
      courseSession: courseSession || undefined,
      text,
    };
  }

  private _renderBlock(block: Block): string | null {
    if (!block || typeof block.type !== "string") return null;
    switch (block.type) {
      case TEXT:
      case HTML: {
        const body = this._stripHtml((block as any).body ?? "");
        return body ? body : null;
      }
      case FREE_TEXT: {
        const header = this._cleanString(block.header);
        const value = this._cleanString(block.value);
        if (!value) return null;
        return header ? `Q: ${header}\nA: ${value}` : `Note: ${value}`;
      }
      case RADIO:
      case SMART_RADIO:
      case SELECT: {
        const header = this._cleanString(block.header);
        const label = this._optionLabelForValue(block, block.value);
        if (!label) return null;
        return header ? `Q: ${header}\nA: ${label}` : `Choice: ${label}`;
      }
      case CHECKBOX:
      case SMART_CHECKBOX: {
        const header = this._cleanString(block.header);
        const values = Array.isArray(block.value)
          ? block.value
          : block.value != null
            ? [block.value]
            : [];
        const labels = values
          .map((v) => this._optionLabelForValue(block, v))
          .filter((l): l is string => !!l);
        if (labels.length === 0) return null;
        const joined = labels.join(", ");
        return header ? `Q: ${header}\nA: ${joined}` : `Choices: ${joined}`;
      }
      case SLIDER: {
        const header = this._cleanString(block.header);
        const value = block.value;
        if (value == null || value === "") return null;
        const unit = (block as any).config?.unit;
        const rendered = unit ? `${value} ${unit}` : `${value}`;
        return header ? `Q: ${header}\nA: ${rendered}` : `Value: ${rendered}`;
      }
      case CONTAINER: {
        const children: string[] = [];
        for (const child of (block as any).blocks ?? []) {
          const rendered = this._renderBlock(child);
          if (rendered) children.push(rendered);
        }
        return children.length ? children.join("\n") : null;
      }
      default:
        return null;
    }
  }

  private _optionLabelForValue(
    block: Block,
    value: unknown,
  ): string | null {
    if (value == null) return null;
    const options: any[] = Array.isArray((block as any).options)
      ? (block as any).options
      : [];
    const match = options.find((opt) => opt?.value === value);
    if (match?.label) return this._cleanString(match.label);
    if (typeof value === "string" || typeof value === "number") {
      return this._cleanString(value);
    }
    return null;
  }

  private _stripHtml(input: string): string {
    if (!input) return "";
    const text = input
      .replace(/<\s*br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
      .replace(/<[^>]+>/g, "");
    return this._cleanString(text);
  }

  private _cleanString(input: unknown): string {
    if (input == null) return "";
    const s = typeof input === "string" ? input : String(input);
    return s.replace(/\s+/g, " ").trim();
  }
}
